import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SoftBox from "components/SoftBox";
import Loader from "examples/Loader";
import CreateModal from "./components/CreateModal";
import AppData from "./components/data-tables/data/dataTableData";
import "./style.css";
import { useAccountsList, useDeleteAccount } from "queries/accounts";
import { useAgencyInfo } from "context/agency";
import Tabs from "../components/Tabs";
import { Delete } from "@mui/icons-material";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import { useUserInfo } from "context/user";
import FreeAccountTemplate from "./components/FreeAccountTemplate";
import { useQueryClient } from "@tanstack/react-query";

function Accounts() {
  const [agency] = useAgencyInfo();
  const [showModal, setShowModal] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const { products, apps, loader } = AppData("accounts");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const userEmail = useUserInfo()[3];
  const queryClient = useQueryClient();

  const [docsCount, setDocsCount] = useState({
    all: 0,
    premium: 0,
    free: 0,
    cancel_request: 0,
    cancel: 0,
  });

  const handleCountChange = (type, count) => {
    setDocsCount((prev) => ({ ...prev, [type]: count }));
  };

  const { mutateAsync: deleteAccount } = useDeleteAccount();

  const handleDeleteAssistant = useCallback((itemId, setCurrentPage) => {
    setCurrentItemId(itemId);
    setShowModal(true);
  }, []);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true); // Show loader
    try {
      await deleteAccount(`_id=${currentItemId}&email=${userEmail}`, {
        onSuccess: ({ response }) => {
          if (response) {
            setShowModal(false);
          } else {
            alert("Not deleted");
          }
          queryClient.invalidateQueries({ queryKey: ["accounts"] })
        },
      });
    } catch (error) {
      alert(error?.message || "Not deleted");
    } finally {
      setDeleteLoading(false); // Hide loader
    }
  };

  return loader ? (
    <Loader />
  ) : (
    <div className="container-alignment">
      <SoftBox p={3} alignItems="center">
        <div className="d-flex justify-content-between w-100" style={{ margin: "30px  20px 50px" }}>
          <div className="w-75 position-relative">
            <h1 className="headingpage-branding" style={{ marginTop: "30px" }}>
              Account
            </h1>
          </div>
          <div className="w-25 text-end">
            <CreateModal products={products} apps={apps} />
          </div>
        </div>
        <Tabs
          tabsData={[
            {
              label: `All Accounts (${docsCount.all})`,
              content: (
                <NewTable
                  handleDeleteAssistant={handleDeleteAssistant}
                  onCountChange={handleCountChange}
                  type={"all"}
                />
              ),
            },
            {
              label: `Premium Accounts (${docsCount.premium})`,
              content: (
                <NewTable
                  handleDeleteAssistant={handleDeleteAssistant}
                  onCountChange={handleCountChange}
                  type={"premium"}
                />
              ),
            },
            {
              label: `Pay offline Accounts (${docsCount.free})`,
              content: (
                <NewTable
                  handleDeleteAssistant={handleDeleteAssistant}
                  onCountChange={handleCountChange}
                  type={"free"}
                />
              ),
            },
            {
              label: `Cancel Request (${docsCount.cancel_request})`,
              content: (
                <NewTable
                  handleDeleteAssistant={handleDeleteAssistant}
                  onCountChange={handleCountChange}
                  type={"cancel_request"}
                />
              ),
            },
            {
              label: `Cancelled Accounts (${docsCount.cancel})`,
              content: (
                <NewTable
                  handleDeleteAssistant={handleDeleteAssistant}
                  onCountChange={handleCountChange}
                  type={"cancel"}
                />
              ),
            },
            {
              label: `Free Account Template`,
              content: <FreeAccountTemplate />,
            },
          ]}
        />
      </SoftBox>

      <DeleteConfirmationModal
        open={showModal}
        onClose={() => setShowModal(false)} // Close modal
        onDelete={handleConfirmDelete} // Trigger the delete action
        deleteLoading={deleteLoading}
      />
    </div>
  );
}

const NewTable = ({ handleDeleteAssistant, onCountChange, type }) => {
  const [agency] = useAgencyInfo();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState(""); // State for search input - instant typing feedback
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const itemsPerPage = 10; // Number of items per page
  const maxButtonsToShow = 5; // Number of pagination buttons to display at a time

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 1000);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // A fresh search resets back to page 1, same as the old debounced effect did.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const queryType = type !== "all" ? type : "";

  const { data, isFetching } = useAccountsList({
    agencyId: agency._id,
    type: queryType,
    page: currentPage,
    search: debouncedSearch,
    limit: itemsPerPage,
  });

  useEffect(() => {
    if (data) onCountChange(type, data.totalRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.totalRecords]);

  const totalPages = data?.totalpages || 1;

  const handlePageChange = (pageNumber) => {
    if (pageNumber === currentPage) return;
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Calculate start and end of pagination buttons
  const startPage = useMemo(
    () => Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1),
    [currentPage]
  );
  const endPage = useMemo(
    () => Math.min(startPage + maxButtonsToShow - 1, totalPages),
    [startPage, totalPages]
  );

  const currentItems = data?.data;

  if (isFetching) return <Loader />;

  if (!Array.isArray(data?.data)) {
    return <p className="text-center">No record found ...</p>;
  }

  return (
    <>
      {/* Search Input */}

      <div
        style={{
          position: "relative",
          marginBottom: "20px",
          display: "flex",
          width: "30%",
          float: "right",
        }}
      >
        <div style={{ position: "absolute", left: "10px", top: "20px" }}>
          <svg
            width="25"
            height="25"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_282_92)">
              <path
                d="M15 13.2955L12.3767 10.7727C13.1166 9.68182 13.5202 8.31818 13.5202 6.88636C13.5202 3.06818 10.4933 0 6.79372 0C3.09417 0 0 3.06818 0 6.88636C0 10.7045 3.02691 13.7727 6.79372 13.7727C8.27354 13.7727 9.61883 13.2955 10.6951 12.4773L13.3184 15.0682L15 13.2955ZM2.35426 6.88636C2.35426 4.43182 4.30493 2.45455 6.72646 2.45455C9.14798 2.45455 11.0987 4.43182 11.0987 6.88636C11.0987 9.34091 9.14798 11.3182 6.72646 11.3182C4.3722 11.3182 2.35426 9.34091 2.35426 6.88636Z"
                fill="#E5E5E5"
              />
            </g>
            <defs>
              <clipPath id="clip0_282_92">
                <rect width="15" height="15" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search by  name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: "30px 45px",
            width: "100%",
            fontSize: "18px",
            boxSizing: "border-box",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        />
      </div>

      <div className="w-100">
        <div className="d-flex w-100">
          <p className="p-20-px w-20-per text-left">Name</p>
          <p className="p-20-px w-20-per text-left">Type</p>
          <p className="p-20-px w-20-per text-left">Status</p>
          <p className="p-20-px w-20-per text-left">Reason</p>
          <p className="p-20-px w-20-per text-left">Address</p>
          <p className="p-20-px w-20-per text-left">Phone #</p>
          <p className="p-20-px w-20-per text-left"></p>
        </div>
        <div>
          {currentItems.filter((item) => item.business_name !== "Bevis").length > 0 ? (
            currentItems
              ?.filter((item) => item.business_name !== "Bevis")
              ?.map((item, index) => (
                <div
                  style={{ position: "relative", display: "flex", alignItems: "center" }}
                  key={index}
                >
                  <Link
                    to={`../account-detail/${item.id}`}
                    className="equalheight d-flex w-100 tablebody-row"
                  >
                    <div className="p-20-px text-black text-bold text-left w-20-per">
                      {item.business_name}
                    </div>
                    <div className="p-20-px text-black text-left w-20-per">
                      <span className="alert-success-new">
                        {item?.type === "premium" ? item?.type : "Pay offline"}
                      </span>
                    </div>
                    <div className="p-20-px text-black text-left w-20-per">{item?.status}</div>
                    <div
                      className="p-20-px text-black text-left w-20-per"
                      style={{
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item?.reason}
                    </div>

                    <div className="p-20-px text-black text-left w-20-per">
                      {item?.address || ""}
                    </div>
                    <div className="p-20-px text-black text-left w-20-per">
                      {item?.phone_number}
                    </div>

                    <div className="p-20-px text-black text-right w-20-per">{`>`}</div>
                  </Link>

                  <span
                    onClick={() => handleDeleteAssistant(item._id, setCurrentPage)}
                    style={{
                      position: "absolute",
                      right: "100px",
                      marginTop: "20px",
                      padding: "8px 10px 2px 10px",
                      borderRadius: "6px",
                      color: "white",
                      cursor: "pointer",
                      background: "red",
                    }}
                  >
                    <Delete />
                  </span>
                </div>
              ))
          ) : (
            <p className="text-center">No accounts found</p>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            opacity: currentPage === 1 ? 0.6 : 1,
          }}
        >
          Previous
        </button>

        {[...Array(endPage - startPage + 1)].map((_, index) => {
          const pageNumber = startPage + index;
          return (
            <button
              key={pageNumber}
              onClick={() => handlePageChange(pageNumber)}
              style={{
                padding: "8px 16px",
                margin: "0 5px",
                backgroundColor: pageNumber === currentPage ? "black" : "white",
                color: pageNumber === currentPage ? "white" : "black",
                border: pageNumber === currentPage ? "1px solid white" : "1px solid black",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {pageNumber}
            </button>
          );
        })}

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            opacity: currentPage === totalPages ? 0.6 : 1,
          }}
        >
          Next
        </button>
      </div>
    </>
  );
};

export default Accounts;
